import React, { useCallback, useEffect, useMemo, useState } from "react";
import "@/styles/Modal.css";
import { useFormContext, useListing } from "@/providers";
import { axiosInstanceWithToken } from "@/helpers/axios";

const SearchModal = ({ filters, setFilters }) => {

  const { watch, register } = useFormContext();
  const { getFilteredListings } = useListing();

  const [features, setFeatures] = useState([]);
  const [priceOpen, setPriceOpen] = useState(false);
  const [sizeOpen, setSizeOpen] = useState(false);
  const [subFeatureOpen, setSubFeatureOpen] = useState({});
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [subFeatures, setSubFeatures] = useState([]);
  const [selectedSubFeatures, setSelectedSubFeatures] = useState([]);

  const handleFeatureSelect = async (event) => {
    const featureId = event.target.value;
    setSelectedFeature(featureId);
    setSelectedFeatures((prev) =>
      prev.includes(featureId)
        ? prev.filter((id) => id !== featureId) // Remove if it exists
        : [...prev, featureId] // Add if it doesn't exist
    );

    setSubFeatureOpen(true); // Automatically open sub-features when a feature is selected
    if (featureId) {
      try {
        const subFeaturesData = await getSubFeatures(featureId); // Fetch sub-features
        setSubFeatures(subFeaturesData);
      } catch (error) {
        console.error("Error fetching sub-features:", error);
      }
    } else {
      setSubFeatures([]); // Clear sub-features if no feature is selected
    }
  };

  const getFeatures = () => {
    axiosInstanceWithToken
      .get(`/feature`)
      .then((res) => {
        setFeatures(res.data?.data || []); // Ensure amenity is always an array
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const getSubFeatures = (id) => {
    axiosInstanceWithToken
      .get(`/feature/${id}`)
      .then((res) => {
        // console.log("subfeatures", res?.data);
        setSubFeatures(res?.data?.data?.feature_detail || []); // Ensure amenity is always an array
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    // GetData1();
    // GetData2();
    getFeatures();
  }, []);

  const valueGetFeature = (e) => {
    const { id, checked, name } = e.target;

    setSelectedSubFeatures((prev) => {
      let updatedSubFeatures = [...prev];

      if (checked) {
        // Add the feature if it's checked, but ensure no duplicates
        if (!updatedSubFeatures.some(item => item.id.toString() === id.toString())) {
          updatedSubFeatures.push({ id, name });
        }
      } else {
        // Remove the feature if it's unchecked
        updatedSubFeatures = updatedSubFeatures.filter(item => item.id.toString() !== id.toString());
      }

      return updatedSubFeatures;
    });

  };

  const handleFilterSubmit = useCallback(async () => {
    let data = watch();
    data = {
      ...data,
      features: selectedFeatures?.join(", "),
      sub_features: selectedSubFeatures?.map((item) => item.id).join(", ")
    }


    await getFilteredListings(data, true);
  }, [getFilteredListings, selectedFeatures, selectedSubFeatures, watch]);

  // Memoized Sub-Feature List
  const subFeatureList = useMemo(() => {
    if (!subFeatureOpen || selectedFeature === null) return null;

    return (
      <div className="p-4 bg-gray-50 rounded-md shadow-md mt-2 h-[150px] overflow-y-scroll">
        <ul className="flex-wrap gap-3 space-y-2 d-flex">
          {subFeatures?.map((subItem) => (
            <li key={subItem?.id} className="flex items-center space-x-2">
              <input
                id={subItem?.id}
                type="checkbox"
                name={subItem?.sub_heading}
                onChange={valueGetFeature}
                className="text-blue-600 form-checkbox"
              />
              <label htmlFor={subItem?.id} className="text-gray-700">
                {subItem?.sub_heading}
              </label>
            </li>
          ))}
        </ul>
      </div>
    );
  }, [subFeatureOpen, selectedFeature, subFeatures]);

  // Memoized Selected Sub-Feature List
  const selectedSubFeatureList = useMemo(() => {
    if (selectedSubFeatures.length === 0) return null;

    return (
      <div className="p-4 bg-gray-50 rounded-md shadow-md mt-2 h-[150px] overflow-y-scroll">
        <h3>Selected Features</h3>
        <ul className="flex-wrap gap-3 space-y-2 d-flex">
          {selectedSubFeatures?.map((subItem) => (
            <li key={subItem?.id} className="flex items-center space-x-2">
              <input
                id={subItem?.id}
                type="checkbox"
                checked
                name={subItem?.name}
                onChange={valueGetFeature}
                className="text-blue-600 form-checkbox"
              />
              <label htmlFor={subItem?.id} className="text-gray-700">
                {subItem?.name}
              </label>
            </li>
          ))}
        </ul>
      </div>
    );
  }, [selectedSubFeatures]);


  // Validation for dimensions fields
  const validateDimensions = (value) => {
    if (!value) return true; // Optional field
    const regex = /^\d+x\d+$/; // Matches "123x12"
    return regex.test(value) || "Dimensions must be in the format 'number x number' (e.g., 123x12)";
  };


  const colourOptions = [
    { value: "Asphalt", label: "Asphalt" },
    { value: "Concrete", label: "Concrete" },
    { value: "Grass", label: "Grass" },
    { value: "Gravel", label: "Gravel" },
    { value: "Ice", label: "Ice" },
    { value: "Sand", label: "Sand" },
    { value: "Snow", label: "Snow" },
    { value: "Water", label: "Water" },
    { value: "Turf", label: "Turf" },
    { value: "Other", label: "Other" },
    { value: "none", label: "None" },
  ];


  return (
    <>
      {filters ? (
        <>
          <div
            className="modal !w-full !left-0 fade show inset-0 flex items-center justify-center sm:justify-start !h-[100vh]"
            id="gridSystemModal"
            tabIndex={-1}
            aria-labelledby="gridModalLabel"
            style={{ display: "block" }}
            aria-modal="true"
            role="dialog"
          >
            <div className="modal-dialog !py-0">
              <div className="modal-content">
                <div className="modal-body h-[400px] overflow-scroll">
                  <div className="container-fluid bd-example-row">
                    {/* Size and Space Dropdown */}
                    <div className="w-full">

                      {/* Helicopter Landing Allowed */}
                      <div className="w-full mt-4">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            {...register("helicopter_allowed")}
                            className="form-checkbox"
                          />
                          <span>Helicopter Landing Allowed</span>
                        </label>
                      </div>

                      {/* Max Elevation */}
                      <div>
                        <label
                          htmlFor="elevation_feets"
                          className="block mb-2 text-sm font-medium text-gray-700"
                        >
                          Max Elevation (ft)
                        </label>
                        <input
                          type="number"
                          id="elevation_feets"
                          {...register("elevation_feets")}
                          className="w-full p-2 mt-1 text-gray-700 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter max elevation"
                        />
                      </div>

                      {/* Height (e.g., 123x12) */}
                      <div>
                        <label
                          htmlFor="height"
                          className="block mb-2 text-sm font-medium text-gray-700"
                        >
                          Height (e.g., 123x12)
                        </label>
                        <input
                          type="text"
                          id="height"
                          {...register("height", { validate: validateDimensions })}
                          className="w-full p-2 mt-1 text-gray-700 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter height (e.g., 123x12)"
                        />
                      </div>

                      {/* Width (e.g., 345x45) */}
                      <div>
                        <label
                          htmlFor="width"
                          className="block mb-2 text-sm font-medium text-gray-700"
                        >
                          Width (e.g., 345x45)
                        </label>
                        <input
                          type="text"
                          id="width"
                          {...register("width", { validate: validateDimensions })}
                          className="w-full p-2 mt-1 text-gray-700 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter width (e.g., 345x45)"
                        />
                      </div>

                      {/* Surface Type */}
                      <div className="w-full mt-4">
                        <label
                          htmlFor="surface"
                          className="block mb-2 text-sm font-medium text-gray-700"
                        >
                          Surface Type
                        </label>
                        <select
                          id="surface"
                          {...register("surface")}
                          className="w-full p-2 mt-1 text-gray-700 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select Surface Type</option>
                          {colourOptions.map((surface) => (
                            <option value={surface.value}>{surface.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Ground Transportation Dropdown */}
                      <div className="w-full mt-4">
                        <label
                          htmlFor="ground_transportation"
                          className="block mb-2 text-sm font-medium text-gray-700"
                        >
                          Ground Transportation
                        </label>
                        <select
                          id="ground_transportation"
                          {...register("ground_transportation")}
                          className="w-full p-2 mt-1 text-gray-700 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select Transportation</option>
                          <option value="We have a courtesy vehicle  waiting for you!">
                            We have a courtesy vehicle  waiting for you!
                          </option>
                          <option value="We have a vehicle you can rent from us!">
                            We have a vehicle you can rent from us!
                          </option>
                          <option value="Rent a vehicle right in the airport! Their phone number is:">
                            Rent a vehicle right in the airport! Their phone number is:
                          </option>
                          <option value="We will give you a ride to the nearest rental vehicle agency!">
                            We will give you a ride to the nearest rental vehicle agency!
                          </option>
                          <option value="We have a deal with the FBO so you can keep the vehicle your whole stay!">
                            We have a deal with the FBO so you can keep the vehicle your whole
                            stay!
                          </option>
                          <option value="You don't need a vehicle here!">
                            You don't need a vehicle here!
                          </option>
                        </select>
                      </div>

                      {/* Smoking, Pets, Parties, and Children Allowed */}
                      <div className="w-full mt-4">
                        <label className="block text-sm font-medium text-gray-700">
                          Allowed
                        </label>
                        <div className="grid grid-cols-2 gap-4 mt-2">
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              {...register("smoking_allowed")}
                              className="form-checkbox"
                            />
                            <span>Smoking</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              {...register("pet_allowed")}
                              className="form-checkbox"
                            />
                            <span>Pets</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              {...register("party_allowed")}
                              className="form-checkbox"
                            />
                            <span>Parties</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              {...register("children_allowed")}
                              className="form-checkbox"
                            />
                            <span>Children</span>
                          </label>
                        </div>

                      </div>

                      {/* Accordion Header */}
                      <button
                        className="flex items-center justify-between w-full py-2 font-medium text-gray-800 border-b"
                        onClick={() => setSizeOpen((open) => !open)}
                      >
                        <span>Size</span>
                        <svg
                          className={`w-5 h-5 transform transition-all duration-300 ${sizeOpen ? "rotate-45 translate-x-1 translate-y-1" : ""}`} // Apply rotation and translation on open
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 5v14M5 12h14"  // Plus icon path
                          />
                        </svg>


                      </button>

                      {/* Accordion Body */}
                      {sizeOpen && (
                        <div className="p-4 space-y-4 bg-gray-50">



                          {/* Bedrooms Selector */}
                          <div>
                            <label
                              htmlFor="no_of_bedrooms"
                              className="block mb-2 text-sm font-medium text-gray-700"
                            >
                              Number of Bedrooms
                            </label>
                            <select
                              id="no_of_bedrooms"
                              name="no_of_bedrooms"
                              {...register("no_of_bedrooms")}
                              className="w-full p-2 mt-1 text-gray-700 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value=''>no of bedrooms</option>
                              {[...Array(20)].map((_, i) => (
                                <option key={i + 1} value={i + 1}>
                                  {i + 1}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Rooms Selector */}
                          <div>
                            <label
                              htmlFor="no_of_rooms"
                              className="block mb-2 text-sm font-medium text-gray-700"
                            >
                              Number of Bathrooms
                            </label>
                            <select
                              id="no_of_bathrooms"
                              name="no_of_bathrooms"
                              {...register("no_of_bathrooms")}
                              className="w-full p-2 mt-1 text-gray-700 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value=''>no of bathrooms</option>
                              {[...Array(20)].map((_, i) => (
                                <option key={i + 1} value={i + 1}>
                                  {i + 1}
                                </option>
                              ))}
                            </select>
                          </div>

                        </div>
                      )}
                    </div>


                    {/* Type of Space Selector */}
                    <div>
                      <label
                        htmlFor="type_of_space"
                        className="block text-sm text-gray-700 font-sm"
                      >
                        Type of Space
                      </label>
                      <select
                        id="type_of_space"
                        name="type_of_space"
                        {...register("type_of_space")}
                        className="w-full p-2 mt-1 text-gray-700 bg-gray-100 border rounded-md"
                      >
                        <option value=''>Space</option>
                        <option value={"Entire Place"}>Entire Place</option>
                        <option value={"Private Room"}>Private Room</option>
                        <option value={"Shared Room"}>Shared Room</option>
                      </select>
                    </div>

                    {/* Price Dropdown */}
                    <div className="w-full">
                      {/* Accordion Header */}
                      <button
                        className="flex items-center justify-between w-full py-2 font-medium text-gray-800 border-b"
                        onClick={() => setPriceOpen((open) => !open)}
                      >
                        <span>Price</span>
                        <svg
                          className={`w-5 h-5 transform transition-all duration-300 ${priceOpen ? "rotate-45 translate-x-1 translate-y-1" : ""}`} // Apply rotation and translation on open
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 5v14M5 12h14"  // Plus icon path
                          />
                        </svg>


                      </button>

                      {/* Accordion Body */}
                      {priceOpen && (
                        <div className="p-4 space-y-4 bg-gray-50">
                          {/* Min Price Selector */}
                          <div>
                            <label htmlFor="price_min" className="block text-sm font-medium text-gray-700">
                              Minimum Price
                            </label>
                            <select
                              id="price_min"
                              name="price_min"
                              {...register("price_min")}
                              // onChange={handleInputChange}
                              className="w-full p-2 mt-1 text-gray-700 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value=''>Min.</option>
                              <option value="10">$10</option>
                              <option value="20">$20</option>
                              <option value="30">$30</option>
                              <option value="40">$40</option>
                              <option value="60">$60</option>
                              <option value="70">$70</option>
                              <option value="80">$80</option>
                              <option value="90">$90</option>
                              <option value="100">$100</option>
                              <option value="110">$110</option>
                              <option value="120">$120</option>
                              <option value="130">$130</option>
                              <option value="140">$140</option>
                              <option value="150">$150</option>
                              <option value="160">$160</option>
                            </select>
                          </div>

                          {/* Max Price Selector */}
                          <div>
                            <label htmlFor="price_max" className="block text-sm font-medium text-gray-700">
                              Maximum Price
                            </label>
                            <select
                              id="price_max"
                              name="price_max"
                              {...register("price_max")}
                              // onChange={handleInputChange}
                              className="w-full p-2 mt-1 text-gray-700 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value=''>Max.</option>
                              <option value="50">$50</option>
                              <option value="100">$100</option>
                              <option value="125">$125</option>
                              <option value="150">$150</option>
                              <option value="160">$160</option>
                              <option value="200">$200</option>
                              <option value="250">$250</option>
                              <option value="300">$300</option>
                              <option value="400">$400</option>
                              <option value="500">$500</option>
                              <option value="600">$600</option>
                              <option value="700">$700</option>
                              <option value="800">$800</option>
                              <option value="900">$900</option>
                              <option value="1000">$1000</option>
                              <option value="1200">$1200</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>


                    {/* Features Dropdown */}
                    <div className="space-y-4">
                      <div className="border-b">
                        <button
                          className="flex items-center justify-between w-full py-2 text-left text-gray-800 font-large"
                          onClick={() => { }}
                        >
                          <span>Features</span>
                        </button>
                        <div className="w-full">
                          <div className="w-full space-y-4">
                            {/* Features Dropdown */}
                            <select
                              className="w-full p-2 bg-gray-100 border rounded-md"
                              onChange={handleFeatureSelect}
                              defaultValue={selectedFeature}
                            >
                              {features.map((feature) => (
                                <option key={feature.id} value={feature.id}>
                                  {feature.heading}
                                </option>
                              ))}
                            </select>

                            {subFeatureList}

                            {/* Sub-feature List */}
                            {selectedSubFeatureList}

                            {/* Message if no sub-features are available */}
                            {subFeatureOpen && subFeatures?.length <= 0 && (
                              <p className="mt-2 text-gray-500">No sub-features available for this feature.</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    onClick={() => setFilters(false)}
                    type="button"
                    className="btn btn-secondary"
                    data-bs-dismiss="modal"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleFilterSubmit}
                    type="button"
                    // disabled={!isFormValid()}
                    className="btn btn-primary"
                  >
                    Filter
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
};

export default SearchModal;
